import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface InstalledSite {
  site_id: string
  domain: string
  version: string
  status: string
  update_available: boolean
  synced_at: number | string
}

export interface InstalledOnResponse {
  success: boolean
  slug: string
  site_count: number
  active_count: number
  update_available_count: number
  versions: Record<string, number>
  sites: InstalledSite[]
}

export interface SyncSiteResponse {
  success: boolean
  siteId: string
  domain: string
  pluginCount: number
  error?: string
  duration: number
}

export interface SyncPluginResponse {
  success: boolean
  sitesProcessed: number
  sitesSucceeded: number
  sitesFailed: number
  pluginsWritten: number
  errors: string[]
  duration: number
}

export const pluginInventoryKeys = {
  all: ['plugin-inventory'] as const,
  installedOn: (slug: string) => [...pluginInventoryKeys.all, 'installed-on', slug] as const,
}

export function useInstalledOn(slug: string | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pluginInventoryKeys.installedOn(slug ?? ''),
    queryFn: async () => {
      return apiClient.get<InstalledOnResponse>(
        `/api/plugin-inventory/plugins/${encodeURIComponent(slug ?? '')}/sites`
      )
    },
    enabled: !!slug && (opts.enabled ?? true),
  })
}

export function useSyncSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (siteId: string) => {
      return apiClient.post<SyncSiteResponse>(`/api/plugin-inventory/sync/site/${siteId}`, {})
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pluginInventoryKeys.all })
    },
  })
}

export function useSyncPlugin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (slug: string) => {
      return apiClient.post<SyncPluginResponse>(
        `/api/plugin-inventory/sync/plugin/${encodeURIComponent(slug)}`,
        {}
      )
    },
    onSuccess: (_res, slug) => {
      qc.invalidateQueries({ queryKey: pluginInventoryKeys.installedOn(slug) })
    },
  })
}
